/**
 * Reading a message thread the way the portal shows it: one outbound
 * message describes the thread, and a failed one is grouped by why it
 * failed so the page can lead with the problems.
 */
import type { components } from '@/types/apiSchema';

export type MessageThreadApi = components['schemas']['MessageThreadResponse'];
export type MessageApi = components['schemas']['MessageResponse'];
type MessageErrorApi = components['schemas']['MessageErrorResponse'];

/** The three states the list can be filtered by; `Sent` covers `Read`. */
export type OutreachStatus = 'Sent' | 'Pending' | 'Failed';

export const outreachStatuses: OutreachStatus[] = ['Sent', 'Pending', 'Failed'];

/** The Outreach page's status filter in the URL, so a link can open it filtered. */
export const STATUS_PARAM = 'status';

/**
 * The message the thread summary describes: the newest one Finlay sent,
 * newest first by creation then id, exactly as the server picks it.
 */
export function latestOutbound(thread: MessageThreadApi) {
  return [...(thread.messages ?? [])]
    .filter((message) => message.origin === 'Recruiter')
    .sort(
      (a, b) =>
        b.createdAt.localeCompare(a.createdAt) || b.id.localeCompare(a.id),
    )[0];
}

export type FailureReason =
  | 'disconnected'
  | 'limit'
  | 'unreachable'
  | 'skipped'
  | 'unknown';

/**
 * The API returns the raw provider error (`error.type` is a Unipile slug or
 * one of our own `messaging/*` markers), not a closed set of reasons, so the
 * portal keyword-matches it into the five groups a customer can act on.
 * Account problems (the API's `IsAccountRelatedUnipileSendFailure` list and
 * our `messaging/sender_account_unavailable`) all mean reconnecting.
 * Anything unrecognised becomes `unknown`, whose action is a plain resend.
 * Takes a message or a candidate's `reachOutMessage`; both carry `error`.
 * Replace this with the real field once the API groups failures itself.
 */
export function failureReason(
  message: { error?: MessageErrorApi | null } | undefined,
): FailureReason {
  const type = (message?.error?.type ?? '').toLowerCase();
  if (type.endsWith('_skipped')) return 'skipped';
  if (
    /account|credential|disconnect|session|checkpoint|privilege|permission|restricted|action_required|unauthor|expired|reconnect/.test(
      type,
    )
  )
    return 'disconnected';
  // `cannot_resend_yet` is LinkedIn's weekly invitation limit.
  if (/limit|quota|rate|throttl|resend_yet/.test(type)) return 'limit';
  if (/recipient|not_found|unreachable|profile|unavailable/.test(type))
    return 'unreachable';
  return 'unknown';
}

interface ReasonCopy {
  /** A few words for a status chip. */
  label: string;
  title: string;
  hint: string;
  action: 'reconnect' | 'resend' | null;
  tone: 'warn' | 'quiet';
}

export const reasonCopy: Record<FailureReason, ReasonCopy> = {
  disconnected: {
    label: 'Session expired',
    title: 'LinkedIn session expired',
    hint: 'Finlay cannot send until you sign in to LinkedIn again. Nothing is lost; these go out once you reconnect.',
    action: 'reconnect',
    tone: 'warn',
  },
  limit: {
    label: 'Weekly limit',
    title: 'LinkedIn’s weekly invitation limit',
    hint: 'LinkedIn turned the invitation down because this account reached its weekly limit. Finlay doesn’t retry it; resend it once the week is up.',
    action: 'resend',
    tone: 'quiet',
  },
  unreachable: {
    label: 'Unreachable',
    title: 'Profile cannot be reached',
    hint: 'The profile was removed or made private after sourcing. Finlay will not try again.',
    action: null,
    tone: 'quiet',
  },
  skipped: {
    label: 'Skipped',
    title: 'Finlay skipped the follow-up',
    hint: 'The candidate replied or moved on before the follow-up was due, so Finlay did not send it.',
    action: null,
    tone: 'quiet',
  },
  unknown: {
    label: 'Not accepted',
    title: 'LinkedIn did not accept the message',
    hint: 'A temporary error on LinkedIn’s side. Resend once; if it fails again Finlay will tell you.',
    action: 'resend',
    tone: 'warn',
  },
};

/** Order the attention groups appear in: what you can fix comes first. */
export const failureReasons: FailureReason[] = [
  'disconnected',
  'unknown',
  'unreachable',
  'limit',
  'skipped',
];

const outboundKindLabels: Record<
  NonNullable<components['schemas']['MessageThreadOutboundKind']>,
  string
> = {
  ConnectionRequest: 'Connection request',
  FollowUp: 'Follow-up',
  Message: 'Message',
};

export function outboundKindLabel(thread: MessageThreadApi) {
  const kind = thread.summary.latestOutboundKind;
  return kind ? outboundKindLabels[kind] : 'Message';
}

export function candidateName(thread: MessageThreadApi) {
  const { firstName, lastName } = thread.candidate;
  return [firstName, lastName].filter(Boolean).join(' ');
}
